// --- React & Redux
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- Actions & Hooks
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
// --- External UI Libraries
import DataGrid from '@dtci/spellbook/DataGrid';
// --- Components
import { DateMatchCalculationsModal } from '../modal/calculations';
import { MacrosDrawer } from '../macros-drawer';
// ---  Utils
import { datastageApi } from 'datastage/api';
import { filterCriteria } from 'datastage/utils/filterCriteria';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { useDateMathGridUtils } from './utils/gridUtils';
// --- Hooks & Actions
import { useDateMathActions } from 'datastage/store/dateMath/useDateMath';
import { useDateMathCalculationsActions } from 'datastage/store/dateMathCalculations/useDateMathCalculations';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';

/**
 * Date Math Grid
 *
 * Features:
 * - Displays a data grid of date math jobs using AG Grid
 * - Integrates with Redux for date math state management
 * - Handles starting, deleting jobs, and opening calculations modal
 *
 * @returns {JSX.Element}
 */
export const DateMathGrid = () => {
    const agGridApi = useAgGridApi();
    const { jobs, filterForm, dateMathRange, dateMathSort } = useSelector((s) => s.dateMath);
    const { updateDateMathCalculations, updateDateMathCalculationsSearchForm } =
        useDateMathCalculationsActions();
    const { updateDateMath } = useDateMathActions();
    const { showErrorToast, showSuccessToast } = useToasts();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const showSpinner = useSelector((s) => s.spinner.showSpinner);

    // Refreshes the list of date math jobs from the API and updates the state
    const refreshDateMathJobs = useCallback(async () => {
        setLoadingTrue();
        try {
            const data = await datastageApi.dateMathJobs(
                filterCriteria(filterForm, '', 'dateMath'),
                dateMathRange,
                dateMathSort,
            );

            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch date math jobs data.');
                return;
            }

            updateDateMath({
                jobs: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            showErrorToast(error?.message || 'Failed to refresh date math jobs.');
            console.error('Error refreshing date math jobs:', error);
        } finally {
            setLoadingFalse();
        }
    }, [filterForm, dateMathRange, dateMathSort, setLoadingTrue, setLoadingFalse, showErrorToast, updateDateMath]);

    // Opens the date math calculations modal for the selected job
    const onOpenDateMathCalculations = useCallback(
        (job) => {
            if (!job || !job.id) return;

            updateDateMathCalculations({
                selectedJob: job,
                showCalculations: true,
            });

            updateDateMathCalculationsSearchForm({
                key: 'filterForm',
                data: { jobId: job.id },
            });
        },
        [updateDateMathCalculations, updateDateMathCalculationsSearchForm],
    );

    // Handles starting a job and refreshes the job list
    const handleStartJob = useCallback(
        async (job) => {
            setLoadingTrue();
            try {
                const res = await datastageApi.startJob(job);

                if (res?.code) {
                    showErrorToast(res?.message || 'Failed to start job.');
                    return;
                }

                showSuccessToast('Job started successfully.');
                await refreshDateMathJobs();
            } catch (error) {
                showErrorToast(error?.message || 'Failed to start job.');
                console.error('Start job error:', error);
            } finally {
                setLoadingFalse();
            }
        },
        [setLoadingTrue, setLoadingFalse, showErrorToast, showSuccessToast, refreshDateMathJobs],
    );

    // Handles deleting a job and refreshes the job list
    const handleDeleteJob = useCallback(
        async (job) => {
            setLoadingTrue();
            try {
                const res = await datastageApi.deleteJob(job);
                if (res?.code) {
                    showErrorToast(res?.message || 'Failed to delete job.');
                    return;
                }
                showSuccessToast('Job deleted successfully.');
                await refreshDateMathJobs();
            } catch (error) {
                showErrorToast(error?.message || 'Failed to delete job.');
                console.error('Delete job error:', error);
            } finally {
                setLoadingFalse();
            }
        },
        [setLoadingTrue, setLoadingFalse, showErrorToast, showSuccessToast, refreshDateMathJobs],
    );

    // Utilize custom hook for grid utilities
    const { columns } = useDateMathGridUtils({
        columnHandlers: [onOpenDateMathCalculations, handleStartJob, handleDeleteJob],
    });

    return (
        <>
            <DataGrid
                className={styles.dataGrid}
                style={{ overflowX: !jobs ? 'hidden' : undefined }}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: jobs,
                    headerHeight: 45,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No Date Math jobs Found'}
            />
            <DateMatchCalculationsModal />
            <MacrosDrawer />
        </>
    );
};
